import Link from "next/link";
import React, { useState } from "react";
import { Product } from "@/api/product-api";
import { getProductDetailHref } from "@/libs/product/detail-href";
import {
  isSizeLineCard,
  sizeLineDialogProduct,
  sizeLineNotifyProduct,
  sizeLineOf,
} from "@/libs/product/size-line";
import { cn } from "@/libs/utils";
import { useProducts } from "@/services/product/useProductService";
import { useTrans } from "@/utils/useTrans";
import { SkuOptionsDialog } from "@/v2-components/cart-sheet/sku-options-dialog";
import { NotifyMe } from "@/v2-components/pages/tactsuit/NotifyMe";
import { Button } from "@/v2-components/ui/button";

/**
 * 목록 카드의 담기 버튼 자리 — **접힌 사이즈 라인 카드면 사이즈를 고르게 한다** (#5922 R1).
 *
 * 접힌 카드(`isSizeLineCard`)의 SKU 는 대표 사이즈 것이라, 원래 담기 버튼을 그대로 두면 사용자가 고르지
 * 않은 사이즈(보통 S)가 담겼다. 그래서 접힌 카드에서는:
 *
 * | 상태 | 버튼 | 동작 |
 * |---|---|---|
 * | 재고 있는 사이즈가 하나라도 있다 | 「Add to Bag」 | **옵션 다이얼로그**(`SkuOptionsDialog`)에서 사이즈별 수량을 고른다 — 같은 페이지의 다른 액세서리(색 여러 개)와 같은 흐름 |
 * | 전부 품절 | 「Notify me」 | **재입고 알림 모달**(`NotifyMe`)에서 사이즈를 고르고 그 사이즈 제품으로 신청 — 다른 카드의 품절과 같은 흐름 |
 * | 카탈로그 미로딩(첫 렌더 순간) | 「Select Size」 | PDP 로 — 대표 사이즈로 잘못 담거나 알림 걸지 않기 위한 안전한 기본값 |
 *
 * 다이얼로그·모달의 행(사이즈)과 행별 slug 는 `sizeLineDialogProduct` · `sizeLineNotifyProduct` 가 만든다.
 * 둘 다 `slugBySku` 를 함께 넘긴다 — 공용 다이얼로그·모달은 기본으로 카드 제품 slug 하나로 담고 신청해서,
 * 그대로면 M 을 골라도 대표(S) 제품으로 기록된다. 그 외 카드는 `children`(원래 담기 버튼)을 그대로 그린다.
 *
 * 쓰는 곳 = `getAccessories` 소비처 중 **담기 버튼이 있는 카드 2곳**(2026-10-06 열거):
 *
 * | 카드 | 이너글러브 라인 카드가 실제로 오나 |
 * |---|---|
 * | `ProductCard` — `/tactsuit/accessories` 「Glove Accessories」 섹션 | **온다** — 리뷰가 실행으로 본 「S 가 담김」의 실제 자리 |
 * | `AccessoryCard` — 같은 페이지 카테고리 탭 | **지금은 안 온다** — 「All」 탭이 `allWithoutGlove` 라 글러브 액세서리가 빠진다. 탭 매핑이 바뀌면 바로 같은 문제가 되는 자리라 함께 감쌌다 |
 *
 * `/tactsuit/` 그리드 카드(`DisplayCategorizedProducts`)는 원래 PDP 링크라 해당 없다.
 * 라벨은 `gnb` 의 「Add to Bag」·「Notify me」·「Select Size」(7 로케일 기존 키). 모양은 두 담기 버튼과 같은
 * `sm`·`pill`·`blue-fill`·`px-6`.
 */
export function SizeLineCardCta({
  product,
  className,
  children,
}: React.PropsWithChildren<{
  product: Pick<Product, "slug" | "name">;
  className?: string;
}>) {
  const { t } = useTrans(["gnb"]);
  const { data: products } = useProducts();
  const [open, setOpen] = useState(false);

  if (!isSizeLineCard(product)) return <>{children}</>;

  const line = sizeLineOf(product.slug);
  const dialog =
    line && products ? sizeLineDialogProduct(products, line) : null;
  const buttonProps = {
    size: "sm",
    shape: "pill",
    variant: "blue-fill",
    className: cn("px-6", className),
  } as const;

  // 전부 품절 → 다른 카드와 같은 「Notify me」. 스타일도 다른 카드의 품절 버튼과 같다
  // (`NotifyOrAddButton` · `NotifyOrBuy` 가 넘기는 `px-6 text-coolgray-90`).
  const notify =
    !dialog && line && products ? sizeLineNotifyProduct(products, line) : null;
  if (notify) {
    return (
      <NotifyMe
        productSlug={notify.product.slug}
        productOverride={notify.product}
        slugBySku={notify.slugBySku}
        className={cn("px-6 text-coolgray-90", className)}
      />
    );
  }

  if (!dialog) {
    return (
      <Button asChild {...buttonProps}>
        <Link href={getProductDetailHref(product.slug)}>
          {t("Select Size")}
        </Link>
      </Button>
    );
  }

  return (
    <>
      <Button {...buttonProps} onClick={() => setOpen(true)}>
        {t("Add to Bag")}
      </Button>
      <SkuOptionsDialog
        open={open}
        product={dialog.product}
        slugBySku={dialog.slugBySku}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
